import React, { useEffect, useRef, useState } from 'react';
import { collection, doc, getDoc, onSnapshot, orderBy, query, updateDoc } from 'firebase/firestore';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import { Howl } from 'howler';
import { db } from '../../../firebase';

const CommandeNotificationProvider = () => {
  const [selectedCommande, setSelectedCommande] = useState(null);
  const [showNotificationPopup, setShowNotificationPopup] = useState(false);
  const [showEnableSoundPopup, setShowEnableSoundPopup] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [errorPopup, setErrorPopup] = useState('');
  const [showErrorModal, setShowErrorModal] = useState(false);

  const soundRef = useRef(null);
  const isFirstLoadRef = useRef(true);
  const playSoundRef = useRef(false);

  const showError = (message) => {
    setErrorPopup(message);
    setShowErrorModal(true);
  };

  const playNotificationSound = () => {
    if (!playSoundRef.current) return;

    if (!soundRef.current) {
      soundRef.current = new Howl({
        src: [
          'https://firebasestorage.googleapis.com/v0/b/restaurant-426210.appspot.com/o/sonneries%2FCOMCell_Iphone%20sonnerie%20temps%20qui%20passe%20(ID%200449)_LS.wav?alt=media&token=3eab17ea-8ded-4bfb-a68d-29fa8ba1f804',
        ],
        loop: true,
        volume: 1.0,
      });
    }

    soundRef.current.play();
  };

  const stopNotificationSound = () => {
    if (soundRef.current) {
      soundRef.current.stop();
    }
  };

  const handleEnableSound = () => {
    playSoundRef.current = true;
    setShowEnableSoundPopup(false);
  };

  useEffect(() => {
    const commandesQuery = query(
      collection(db, 'commande'),
      orderBy('id', 'desc')
    );

    const unsubscribe = onSnapshot(
      commandesQuery,
      (snapshot) => {
        if (isFirstLoadRef.current) {
          isFirstLoadRef.current = false;
          return;
        }

        snapshot.docChanges().forEach((change) => {
          if (change.type !== 'added') return;

          const latestCommande = {
            id: change.doc.id,
            ...change.doc.data(),
          };

          if (
            latestCommande.statut_commande !== 'Accepté' &&
            latestCommande.statut_commande !== 'Refusé'
          ) {
            setSelectedCommande(latestCommande);
            setShowNotificationPopup(true);
            playNotificationSound();
          }
        });
      },
      (error) => {
        console.error('Erreur listener commandes :', error);
        showError("Impossible d'activer les notifications de commandes.");
      }
    );

    return () => {
      unsubscribe();
      stopNotificationSound();
    };
  }, []);

  const getEmailContent = async (id) => {
    const emailDoc = await getDoc(doc(db, 'email', id));
    return emailDoc.exists() ? emailDoc.data() : null;
  };

  const handleCapturePayment = async (paymentIntentId) => {
    const response = await fetch('https://api-7zyk3qftka-uc.a.run.app/api/capture-payment', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ paymentIntentId }),
    });

    const data = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(data.error || 'Erreur lors de la capture du paiement.');
    }

    return data;
  };

  const handleCancelPayment = async (paymentIntentId) => {
    const response = await fetch('https://api-7zyk3qftka-uc.a.run.app/api/cancel-payment', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ paymentIntentId }),
    });

    const data = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(data.error || "Erreur lors de l'annulation du paiement.");
    }

    return data;
  };

  const updateCommandeStatus = async (newStatus) => {
    if (!selectedCommande) return;

    stopNotificationSound();
    setIsUpdating(true);

    try {
      const commandeRef = doc(db, 'commande', selectedCommande.id);

      if (!selectedCommande.paymentIntentId) {
        throw new Error('paymentIntentId manquant.');
      }

      if (selectedCommande.statut_paiement !== 'requires_capture') {
        throw new Error(
          `Paiement non capturable. Statut actuel : ${selectedCommande.statut_paiement}`
        );
      }

      if (newStatus === 'Accepté') {
        const captureResult = await handleCapturePayment(selectedCommande.paymentIntentId);

        await updateDoc(commandeRef, {
          statut_commande: 'Accepté',
          statut_paiement: captureResult.status,
        });

        const emailContent = await getEmailContent('confirmationCommande');

        if (emailContent) {
          await fetch('https://api-7zyk3qftka-uc.a.run.app/api/send-email', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              email: selectedCommande.email,
              nom: selectedCommande.nom,
              prenom: selectedCommande.prenom,
              plats: selectedCommande.plats,
              total: selectedCommande.montant,
              date_service: selectedCommande.date_service,
              objet: emailContent.objet,
              emailContent1: emailContent.texte,
              emailContent2: emailContent.texte2,
            }),
          });
        }
      }

      if (newStatus === 'Refusé') {
        const cancelResult = await handleCancelPayment(selectedCommande.paymentIntentId);

        await updateDoc(commandeRef, {
          statut_commande: 'Refusé',
          statut_paiement: cancelResult.status,
        });

        const emailAnn = await getEmailContent('annulationCommande');

        if (emailAnn) {
          await fetch('https://api-7zyk3qftka-uc.a.run.app/api/email-annulation', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              email: selectedCommande.email,
              nom: selectedCommande.nom,
              prenom: selectedCommande.prenom,
              objet: emailAnn.objet,
              emailContent: emailAnn.texte,
            }),
          });
        }
      }

      setShowNotificationPopup(false);
      setSelectedCommande(null);
    } catch (error) {
      console.error(error);
      showError(error.message || 'Impossible de mettre à jour la commande.');
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <>
      <Modal show={showEnableSoundPopup} onHide={() => setShowEnableSoundPopup(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Activer les notifications sonores</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Pour recevoir les alertes des nouvelles commandes, veuillez activer le son.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={handleEnableSound}>
            Activer le son
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showNotificationPopup}
        onHide={() => {
          setShowNotificationPopup(false);
          stopNotificationSound();
        }}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Nouvelle commande</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {selectedCommande && (
            <>
              <p><strong>Commande :</strong> #{selectedCommande.id}</p>
              <p><strong>Client :</strong> {selectedCommande.prenom} {selectedCommande.nom}</p>
              <p><strong>Date de récupération :</strong> {selectedCommande.date_service}</p>
              <p><strong>Montant :</strong> {selectedCommande.montant} €</p>
              <p><strong>Statut paiement :</strong> {selectedCommande.statut_paiement}</p>

              <p><strong>Articles :</strong></p>
              <ul>
                {selectedCommande.plats?.map((plat, index) => (
                  <li key={index}>
                    {plat.quantite} x {plat.nom}
                  </li>
                ))}
              </ul>
            </>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button
            disabled={isUpdating}
            variant="success"
            onClick={() => updateCommandeStatus('Accepté')}
          >
            {isUpdating ? 'Traitement...' : 'Accepter'}
          </Button>

          <Button
            disabled={isUpdating}
            variant="danger"
            onClick={() => updateCommandeStatus('Refusé')}
          >
            {isUpdating ? 'Traitement...' : 'Refuser'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showErrorModal} onHide={() => setShowErrorModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Erreur</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{errorPopup}</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowErrorModal(false)}>
            Fermer
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default CommandeNotificationProvider;